import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { OWNER_EMAIL, SALES_MANAGER_EMAIL, getUserRole, hasPermission } from '../utils/constants';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up disabled for single-owner setup
  async function signup() {
    throw new Error('Sign up is disabled. Only the owner can sign in.');
  }

  // Sign in function
  async function signin(email, password) {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const userEmail = cred.user?.email?.toLowerCase();
    
    // Check if user is authorized (owner or sales manager)
    const isOwner = OWNER_EMAIL && userEmail === OWNER_EMAIL.toLowerCase();
    const isSalesManager = SALES_MANAGER_EMAIL && userEmail === SALES_MANAGER_EMAIL.toLowerCase();
    
    if (!isOwner && !isSalesManager) {
      // Immediately sign out unauthorized accounts
      await signOut(auth);
      throw new Error('Access restricted. Only authorized accounts can sign in.');
    }
    return cred;
  }

  // Sign out function
  function logout() {
    setUserProfile(null);
    return signOut(auth);
  }

  // Reset password function
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Update user profile
  async function updateUserProfile(profileData) {
    if (!currentUser) throw new Error('No user logged in');
    
    const updatedProfile = {
      ...userProfile,
      ...profileData,
      updatedAt: new Date()
    };
    
    await setDoc(doc(db, 'users', currentUser.uid), updatedProfile, { merge: true });
    setUserProfile(updatedProfile);
    
    return updatedProfile;
  }

  // Load user profile from Firestore
  async function loadUserProfile(user) {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const profile = userDoc.data();
        setUserProfile(profile);
        return profile;
      } else {
        // Create a basic profile if it doesn't exist
        const basicProfile = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          businessName: '',
          gstNumber: '',
          address: '',
          phone: '',
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        await setDoc(doc(db, 'users', user.uid), basicProfile);
        setUserProfile(basicProfile);
        return basicProfile;
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      return null;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userEmail = user.email?.toLowerCase();
        const isOwner = OWNER_EMAIL && userEmail === OWNER_EMAIL.toLowerCase();
        const isSalesManager = SALES_MANAGER_EMAIL && userEmail === SALES_MANAGER_EMAIL.toLowerCase();
        
        if (!isOwner && !isSalesManager) {
          // Enforce authorized access: sign out and block
          await signOut(auth);
          setCurrentUser(null);
          setUserProfile(null);
        } else {
          setCurrentUser(user);
          await loadUserProfile(user);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    signin,
    logout,
    resetPassword,
    updateUserProfile,
    loadUserProfile,
    getUserRole: () => currentUser ? getUserRole(currentUser.email) : null,
    hasPermission: (permission) => currentUser ? hasPermission(currentUser.email, permission) : false
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
